import React, { useMemo } from "react";
import KC_Card from "../../../../designsystem/KC_Card/KC_Card";
import {
  type ActiveEvent,
  EventConfig,
  GameTypes
} from "kadow-jetpack-model";
import { isPerformanceGameHiddenByDisabledMonthlyLottery } from "../../../../utils/helpers/performanceGameAvailability";
import { useEventsSelector } from "../../../../redux/actions/wheel";
import "./styles.scss";
import KC_UIText from "../../../../designsystem/typography/KC_UIText/KC_UIText";
import TSUtils from "../../../../utils/helpers/TSUtils";
import {
  updateJetpackConfigThunk,
  useJetpackConfigSelector
} from "../../../../redux/jetpackConfig";
import { useDispatch } from "react-redux";
import { useAccountToDisplaySelector } from "../../../../redux/actions/user";
import GameCardPreview from "../../../23_GameCardPreview/GameCardPreview";
import { LANGUAGE, tt } from "../../../../utils/i18n/i18n";
import { getDisplayNameForGameType } from "../../../../utils/helpers/getDisplayNameForGameType";
import Dot from "../../../../components/Dot/Dot";
import moment from "moment-timezone";

interface EventSectionPropsI {
  activeEvents: ActiveEvent[];
}

export default function EventSection(props: EventSectionPropsI) {
  const events = useEventsSelector();
  const dispatch = useDispatch();
  const { accountToDisplay } = useAccountToDisplaySelector();
  const config = useJetpackConfigSelector();

  const handleDelete = (ae: ActiveEvent) => {
    if (!config?.activeEvents?.length) {
      return;
    }

    if (!confirm(tt("confirm_deletion"))) {
      return;
    }

    const idx =
      config?.activeEvents?.findIndex(
        (configAE) =>
          configAE.id === ae.id &&
          configAE.sts === ae.sts &&
          configAE.ets === ae.ets &&
          configAE.gameType === ae.gameType
      ) ?? -1;

    if (idx === -1) {
      return;
    }

    const newActiveEvents = [...config.activeEvents];

    /*
      Scheduled events are re-added by the batch, so instead of removing them
      we flag them as disabled (d) to keep them out of the config.
    */
    if (ae.s) {
      newActiveEvents[idx] = { ...newActiveEvents[idx], d: true };
    } else {
      newActiveEvents.splice(idx, 1);
    }

    dispatch(
      updateJetpackConfigThunk(
        { activeEvents: newActiveEvents },
        accountToDisplay?.trackingID,
        accountToDisplay?.token
      )
    );
  };

  /*
    Need to make a copy here because props.activeEvents is immutable
  */
  const eventsToDisplay = useMemo(() => {
    if (!props.activeEvents) {
      return [];
    }
    const copy = [...(props.activeEvents || [])].filter((ae) => !ae.d);
    copy.sort((a, b) => TSUtils.sortNumbers(a, b, "sts", "asc"));
    return copy;
  }, [props.activeEvents]);

  const NOW_TS = useMemo(() => {
    return moment().unix();
  }, []);

  const GAMES = useMemo(() => {
    return eventsToDisplay
      .map((ae, idx) => {
        if (!ae.gameType) {
          return null;
        }

        const eventConfig = events?.find((e) => e._id === ae.id);

        if (!eventConfig) {
          return null;
        }

        const skin = eventConfig.skins.find((s) => s.game === ae.gameType);

        if (!skin) {
          return null;
        }

        const isOngoing =
          !!eventConfig.permanent || (NOW_TS >= ae.sts && NOW_TS <= ae.ets);

        const gt = ae.gameType as unknown as GameTypes;

        if (gt && isPerformanceGameHiddenByDisabledMonthlyLottery(gt, config)) {
          return null;
        }

        return (
          <KC_Card
            key={`${ae.id}-${idx}`}
            title={getDisplayNameForGameType(ae.gameType)}
            boxShadow={false}
            description=""
            useTwicPics={true}
            isGameCardPreview={true}
            className="border"
            canDelete={true}
            isSelected={true}
            handleDelete={() => handleDelete(ae)}
            children={
              <>
                <GameCardPreview
                  gameCardConfig={skin.gameCardConfig}
                  eventId={ae.id}
                />
                <div className="d-flex align-items-center">
                  <Dot
                    color={
                      !!eventConfig.permanent
                        ? "green"
                        : isOngoing
                          ? "green"
                          : "red"
                    }
                  />
                  <KC_UIText
                    className="ml-2"
                    weight="reg"
                    text={
                      !!eventConfig.permanent ? (
                        <KC_UIText
                          weight="reg"
                          text={tt("active_permanently")}
                        />
                      ) : (
                        formatStartAndEndDatesForActiveEvent(ae)
                      )
                    }
                  />
                </div>
                {!!ae.s && (
                  <div className="d-flex align-items-center mt-1">
                    <KC_UIText weight="bold" text={tt("scheduled")} />
                    <i
                      className="fas fa-info-circle ml-2"
                      title={tt("scheduled_tooltip")}
                    />
                  </div>
                )}
              </>
            }
          />
        );
      })
      .filter((s) => !!s);
  }, [config, eventsToDisplay]);

  return (
    <div className="d-flex flex-column st-gap">{GAMES.map((game) => game)}</div>
  );
}

export const getEventName = (event?: EventConfig, gameType?: string) => {
  if (!event || !event.name) {
    if (gameType) {
      return getDisplayNameForGameType(gameType);
    }
    return "";
  }
  return event.nameL?.[LANGUAGE] || event.name;
};

function formatStartAndEndDatesForActiveEvent(ae: ActiveEvent) {
  return (
    <div>
      {tt("event_display_from")}{" "}
      <KC_UIText weight="bold" text={TSUtils.formatDate(ae.sts)} />{" "}
      {tt("event_display_to")}{" "}
      <KC_UIText weight="bold" text={TSUtils.formatDate(ae.ets)} />
    </div>
  );
}
